import axios from "axios";
import { INTEGRATION_SERVER_URL } from "../config/config";

/**
 * Thin client over the public checkout endpoints. The response interceptor
 * returns the response body (the { success, data } envelope); each method
 * unwraps `.data` for the caller.
 */
const client = axios.create({
  baseURL: INTEGRATION_SERVER_URL + "/checkout/t1",
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const normalizeError = (error) => {
  if (error.response) {
    const body = error.response.data || {};
    return Promise.reject({
      message: body.message || "Server error",
      status: error.response.status,
      // The lead endpoint returns { success:false, code:"VALIDATION_ERROR", errors }
      // with no `message`; carry both through so the template can map field
      // errors inline and branch on coupon/lead error codes.
      code: body.code,
      errors: body.errors,
    });
  }
  if (error.request) {
    return Promise.reject({
      message: "Network error — unable to connect to server",
      status: 0,
    });
  }
  return Promise.reject({
    message: error.message || "Unknown error occurred",
    status: -1,
  });
};

client.interceptors.response.use((r) => r.data, normalizeError);

/* ------------------------------ lead token ------------------------------ */

/**
 * Per-lead bearer credential, minted server-side by createLead and returned exactly once.
 *
 * Kept in sessionStorage keyed by leadId, because ClassicTemplate persists the leadId and
 * restores it after a refresh: an in-memory-only token would survive the reload as
 * `undefined` while the leadId came back intact, 404-ing the rest of the checkout.
 * sessionStorage matches the lead's lifetime — per tab, and leads expire in 30 minutes.
 */
const LEAD_TOKEN_KEY = (leadId) => `checkout_lead_token_${leadId}`;

const rememberLeadToken = (leadId, leadToken) => {
  if (!leadId || !leadToken) return;
  try {
    sessionStorage.setItem(LEAD_TOKEN_KEY(leadId), leadToken);
  } catch {
    /* private mode / quota — the in-flight call still carries the header below */
  }
};

const readLeadToken = (leadId) => {
  if (!leadId) return null;
  try {
    return sessionStorage.getItem(LEAD_TOKEN_KEY(leadId));
  } catch {
    return null;
  }
};

export const forgetLeadToken = (leadId) => {
  if (!leadId) return;
  try {
    sessionStorage.removeItem(LEAD_TOKEN_KEY(leadId));
  } catch {
    /* ignore */
  }
};

// Header config for a lead-anchored request. Omitted entirely when no token is known, so
// the server returns its normal 404 rather than us sending `undefined` as a value.
const leadAuth = (leadId) => {
  const token = readLeadToken(leadId);
  return token ? { headers: { "x-checkout-lead-token": token } } : undefined;
};

class CheckoutPageApiService {
  /**
   * @param pageId      checkoutPage _id
   * @param accessToken optional pre-apply coupon token from ?accessToken
   * @returns { page, config, products }
   */
  async getPublicPage(pageId, accessToken) {
    const envelope = await client.get(`/page/${pageId}`, {
      params: accessToken ? { accessToken } : undefined,
    });
    return envelope.data;
  }

  /** Server-authoritative totals; called when a coupon is applied. */
  async getSummary(pageId, couponCode, accessToken) {
    const envelope = await client.post(`/${pageId}/summary`, {
      couponCode,
      accessToken,
    });
    return envelope.data;
  }

  /**
   * Form submission → lead. Rejects with { code:"VALIDATION_ERROR", errors } on 400.
   * This is the ONLY response that carries `leadToken`; it is stashed here so callers
   * never have to thread it through, and every later lead-anchored call picks it up.
   */
  async createLead(pageId, formData) {
    const envelope = await client.post(`/${pageId}/lead`, { formData });
    const { leadId, leadToken } = envelope.data;
    rememberLeadToken(leadId, leadToken);
    return envelope.data; // { leadId, leadToken }
  }

  /** Patches an existing lead's customer data (buyer edited the form on return). */
  async updateLeadCustomer(leadId, formData) {
    const envelope = await client.patch(
      `/lead/${leadId}/customer`,
      { formData },
      leadAuth(leadId)
    );
    return envelope.data; // { leadId }
  }

  /** Creates the gateway order. leadId-anchored — config derived server-side. */
  async createOrder(leadId, couponCode, accessToken) {
    const envelope = await client.patch(
      `/lead/${leadId}`,
      { couponCode, accessToken },
      leadAuth(leadId)
    );
    return envelope.data; // { gatewayOrderId, gatewayKey, amount, currency, gateway }
  }

  /** Verifies a completed Razorpay payment. Returns post-payment routing info. */
  async verifyPayment(payload) {
    const envelope = await client.post(
      `/verify`,
      payload,
      leadAuth(payload && payload.leadId)
    );
    return envelope.data; // { transactionId, destinationUrl, accessCode }
  }

  /**
   * Records the end of a payment attempt. outcome "cancelled" (modal dismissed)
   * keeps the lead payable; "failed" (gateway decline) marks it failed. Both stay
   * retryable server-side. Best-effort from the UI's perspective.
   */
  async markPaymentAttemptEnded(leadId, gatewayOrderId, outcome, err) {
    return client.post(
      `/lead/${leadId}/payment-failed`,
      {
        gatewayOrderId,
        outcome,
        errorCode: err && err.code,
        errorDescription: err && err.description,
      },
      leadAuth(leadId)
    );
  }
}

export const checkoutPageApiService = new CheckoutPageApiService();
export default checkoutPageApiService;
